import type { ReactNode } from 'react';
import { Icon, type IconName } from '../icons/icons.js';
import type { AccentTone } from '../dashboard/dashboard.js';
import styles from './overview.module.css';

/**
 * THE MODULE-OVERVIEW ANATOMY, converted from the Stitch Screen 01 markup.
 *
 * WHERE THIS CAME FROM. `01_Tenant_App/Finance/01_Finance_Overview/code.html`,
 * element by element. Each component below names the Stitch node it is, and the
 * CSS module beside it names the Tailwind class every rule came from. The
 * previous Screen 01 was assembled out of the generic patterns — a `<dl>` in a
 * card, a `StatTileRow`, two stacked tables — which is a different screen that
 * happens to carry the same figures.
 *
 * WHY IT IS NOT CALLED FINANCE ANYTHING. The reference draws a module's front
 * page: four facts about the books, four headline figures, the module's
 * functions in columns, and its two most recent record sets side by side. Every
 * module gets one. Finance is the first, so this is where the anatomy lands,
 * and Procurement will not need a second copy of it.
 *
 * NOTHING HERE COMPUTES A FACT. Values arrive formatted, deltas arrive as the
 * server's two figures already divided, donut segments arrive as the server's
 * own dimensioned points. The arithmetic below turns values into arc lengths
 * and bar heights, which is drawing.
 */

const TONE_CLASS: Record<AccentTone, string> = {
  teal: styles.toneTeal ?? '',
  success: styles.toneSuccess ?? '',
  info: styles.toneInfo ?? '',
  warning: styles.toneWarning ?? '',
  danger: styles.toneDanger ?? '',
  neutral: styles.toneNeutral ?? '',
  accent: styles.toneAccent ?? '',
};

/* =========================================================================
   THE META STRIP — four facts to read before any figure on the page
   ========================================================================= */

/** `grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3.5` */
export function MetaStrip({ children }: { readonly children: ReactNode }) {
  return <div className={styles.metaStrip}>{children}</div>;
}

export interface MetaCardProps {
  readonly icon: IconName;
  readonly tone?: AccentTone;
  readonly label: string;
  /** Already worded by the caller: this renders a fact, it does not read one. */
  readonly value: ReactNode;
}

/** `bg-white border rounded-lg p-3 flex items-center gap-3 shadow-xs` */
export function MetaCard({ icon, tone = 'info', label, value }: MetaCardProps) {
  return (
    <div className={styles.metaCard}>
      <span className={[styles.metaIcon, TONE_CLASS[tone]].join(' ')} aria-hidden="true">
        <Icon name={icon} size={16} />
      </span>
      <span className={styles.metaText}>
        <span className={styles.metaLabel}>{label}</span>
        <span className={styles.metaValue}>{value}</span>
      </span>
    </div>
  );
}

/* =========================================================================
   THE KPI ROW — four headline figures, the page's subject
   ========================================================================= */

/** `grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3.5` */
export function OverviewKpiRow({ children }: { readonly children: ReactNode }) {
  return <div className={styles.kpiRow}>{children}</div>;
}

export interface OverviewKpiCardProps {
  readonly icon: IconName;
  readonly tone?: AccentTone;
  readonly label: string;
  /** Formatted by the caller. A card that formatted money would guess. */
  readonly value?: string | undefined;
  /** Percentage change on the preceding window, from two server figures. */
  readonly changePercent?: number | undefined;
  readonly changeCaption?: string | undefined;
  readonly points?: readonly number[] | undefined;
  /** The server's own reason. A refused figure is never a zero. */
  readonly unavailable?: string | undefined;
}

/**
 * KPI 1, 3 and 4: `p-4 flex flex-col justify-between`.
 *
 * Head is `flex items-start gap-3` — a 40px SOLID badge, the label and the
 * figure. Foot is `mt-4 flex items-end justify-between` — the delta and its
 * caption on the left, the sparkline on the right.
 */
export function OverviewKpiCard({
  icon,
  tone = 'teal',
  label,
  value,
  changePercent,
  changeCaption,
  points,
  unavailable,
}: OverviewKpiCardProps) {
  if (unavailable !== undefined) {
    return (
      <div className={styles.kpiCard}>
        <div className={styles.kpiHead}>
          <span className={[styles.kpiIcon, TONE_CLASS.neutral].join(' ')} aria-hidden="true">
            <Icon name={icon} size={20} />
          </span>
          <span className={styles.kpiText}>
            <span className={styles.kpiLabel}>{label}</span>
            {/*
              THE FIXED HEADING THE PRODUCT ALREADY USES for exactly this, so a
              figure refused here and refused on the dashboard read as one thing
              happening rather than two.
            */}
            <span className={styles.kpiRefusedTitle}>Not available for your access</span>
            <span className={styles.kpiRefused}>{unavailable}</span>
          </span>
        </div>
      </div>
    );
  }

  const rising = changePercent !== undefined && changePercent > 0;
  const falling = changePercent !== undefined && changePercent < 0;

  return (
    <div className={[styles.kpiCard, TONE_CLASS[tone]].join(' ')}>
      <div className={styles.kpiHead}>
        <span className={styles.kpiIcon} aria-hidden="true">
          <Icon name={icon} size={20} />
        </span>
        <span className={styles.kpiText}>
          <span className={styles.kpiLabel}>{label}</span>
          {/*
           * The figure carries its own name because the label above it is a
           * separate element: without this, the only way to ask for one
           * card's total among four is to know which was on screen.
           */}
          <span className={styles.kpiValue} aria-label={`${label} value`}>
            {value ?? '—'}
          </span>
        </span>
      </div>
      {/*
        A CAPTION ALONE IS ENOUGH TO EARN THE FOOT, and it was not before.
        The foot appeared only for a delta or a sparkline, so a card with a real
        caption and no comparison — "14 days past due · INV-2026-0388", which is
        the reference's own third line on an overdue card — rendered the caption
        into nothing. The dashboard's own `KpiCard` has always shown a caption on
        its own (see dashboard.tsx), so this was the two variants disagreeing
        rather than a rule.
        NOT EVERY CARD HAS A COMPARISON TO MAKE. A KPI whose previous-period
        figure the server does not hold must not be given an invented delta just
        to make its caption visible — that would be a fake figure in service of a
        true sentence.
      */}
      {(changePercent !== undefined ||
        changeCaption !== undefined ||
        (points !== undefined && points.length > 1)) && (
        <div className={styles.kpiFoot}>
          <span className={styles.kpiDelta}>
            {changePercent !== undefined && (
              <span
                className={[
                  styles.kpiDeltaValue,
                  rising ? styles.kpiDeltaUp : '',
                  falling ? styles.kpiDeltaDown : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
              >
                <span aria-hidden="true">{rising ? '↑ ' : falling ? '↓ ' : ''}</span>
                {`${String(Math.abs(Math.round(changePercent)))}%`}
              </span>
            )}
            {changeCaption !== undefined && (
              <span className={styles.kpiDeltaCaption}>{changeCaption}</span>
            )}
          </span>
          <Sparkline points={points} />
        </div>
      )}
    </div>
  );
}

/**
 * `flex items-end gap-1 h-7`, bars `w-1.5 rounded-xs`.
 *
 * DECORATIVE, and `aria-hidden` for it: the figure and its delta carry the
 * meaning and the bars repeat it without adding anything.
 */
function Sparkline({ points }: { readonly points?: readonly number[] | undefined }) {
  if (points === undefined || points.length < 2) return null;
  const highest = Math.max(...points, 1);
  return (
    <span className={styles.kpiSparkline} aria-hidden="true" data-testid="sparkline">
      {points.map((point, index) => (
        <span
          key={index}
          className={styles.kpiSparklineBar}
          style={{ height: `${String(Math.max(12, (point / highest) * 100))}%` }}
        />
      ))}
    </span>
  );
}

export interface OverviewSegment {
  readonly label: string;
  readonly value: number;
  readonly tone: AccentTone;
}

export interface OverviewBreakdownCardProps {
  readonly icon: IconName;
  readonly tone?: AccentTone;
  readonly label: string;
  readonly value?: string | undefined;
  /** The server's own dimensioned points, turned into arcs and keys. */
  readonly segments: readonly OverviewSegment[];
  /** The word under the ring's total — "Invoices", "Bills". */
  readonly caption?: string | undefined;
}

/**
 * KPI 2: `p-4 flex items-center justify-between` — the stat column and its
 * status keys on the left, an 80px donut on the right.
 *
 * WHY THIS IS A SEPARATE COMPONENT rather than a `chart` slot on the card
 * above: the reference's second card is a different flex axis, not a variant
 * of the first. Its body is a column inside a row, and the ring is a sibling
 * of that column rather than a third row inside it.
 */
export function OverviewBreakdownCard({
  icon,
  tone = 'info',
  label,
  value,
  segments,
  caption,
}: OverviewBreakdownCardProps) {
  const total = segments.reduce((running, segment) => running + segment.value, 0);
  return (
    <div
      className={[styles.kpiCard, styles.kpiCard_breakdown, TONE_CLASS[tone]]
        .filter(Boolean)
        .join(' ')}
    >
      <div className={styles.kpiBreakdownBody}>
        <div className={styles.kpiHead}>
          <span className={styles.kpiIcon} aria-hidden="true">
            <Icon name={icon} size={20} />
          </span>
          <span className={styles.kpiText}>
            <span className={styles.kpiLabel}>{label}</span>
            {/*
             * The figure carries its own name because the label above it is a
             * separate element: without this, the only way to ask for one
             * card's total among four is to know which was on screen.
             */}
            <span className={styles.kpiValue} aria-label={`${label} value`}>
              {value ?? '—'}
            </span>
          </span>
        </div>
        {/* `mt-2.5 space-y-1 text-[11px]`, tighter past the reference's three. */}
        <ul
          className={[styles.statusList, segments.length > 3 ? styles.statusList_dense : '']
            .filter(Boolean)
            .join(' ')}
        >
          {segments.map((segment) => (
            <li
              key={segment.label}
              className={[styles.statusRow, TONE_CLASS[segment.tone]].join(' ')}
            >
              <span className={styles.statusDot} aria-hidden="true" />
              <span className={styles.statusCount}>
                {new Intl.NumberFormat('en-GB').format(segment.value)}
              </span>
              <span className={styles.statusLabel}>{segment.label}</span>
            </li>
          ))}
        </ul>
      </div>
      <Donut segments={segments} total={total} {...(caption === undefined ? {} : { caption })} />
    </div>
  );
}

/**
 * `relative w-20 h-20`, `svg.-rotate-90 viewBox="0 0 36 36"`, arcs drawn as
 * `r="14" stroke-width="4"` circles with a `stroke-dasharray` in circumference
 * units and a negative `stroke-dashoffset` for each segment after the first.
 *
 * `aria-hidden`, because the keys beside it already read every figure in it.
 * A `role="img"` label here would say the same breakdown twice.
 */
function Donut({
  segments,
  total,
  caption,
}: {
  readonly segments: readonly OverviewSegment[];
  readonly total: number;
  readonly caption?: string;
}) {
  const circumference = 2 * Math.PI * 14;
  let consumed = 0;
  return (
    <span className={styles.donutWrap}>
      <svg className={styles.donut} viewBox="0 0 36 36" aria-hidden="true" focusable="false">
        <circle className={styles.donutTrack} cx="18" cy="18" r="14" />
        {total > 0 &&
          segments.map((segment) => {
            const length = (segment.value / total) * circumference;
            const offset = -consumed;
            consumed += length;
            return (
              <circle
                key={segment.label}
                className={[styles.donutArc, TONE_CLASS[segment.tone]].join(' ')}
                cx="18"
                cy="18"
                r="14"
                strokeDasharray={`${String(length)} ${String(circumference)}`}
                strokeDashoffset={String(offset)}
              />
            );
          })}
      </svg>
      <span className={styles.donutCentre}>
        <span className={styles.donutTotal}>{new Intl.NumberFormat('en-GB').format(total)}</span>
        {caption !== undefined && <span className={styles.donutCaption}>{caption}</span>}
      </span>
    </span>
  );
}

/* =========================================================================
   THE MODULE COLUMNS — the module's own functions, grouped
   ========================================================================= */

export interface ModuleSectionProps {
  readonly icon?: IconName;
  readonly title: string;
  readonly subtitle?: string;
  readonly children: ReactNode;
}

/** `section.space-y-3` with a `flex items-center gap-2` heading. */
export function ModuleSection({ icon, title, subtitle, children }: ModuleSectionProps) {
  return (
    <section className={styles.moduleSection}>
      <div className={styles.moduleSectionHead}>
        {icon !== undefined && (
          <span className={styles.moduleSectionIcon} aria-hidden="true">
            <Icon name={icon} size={16} />
          </span>
        )}
        <div className={styles.moduleSectionTitles}>
          <h2 className={styles.moduleSectionTitle}>{title}</h2>
          {subtitle !== undefined && <p className={styles.moduleSectionSubtitle}>{subtitle}</p>}
        </div>
      </div>
      <div className={styles.moduleGrid}>{children}</div>
    </section>
  );
}

export interface ModuleColumnProps {
  readonly icon: IconName;
  readonly tone?: AccentTone;
  readonly title: string;
  readonly blurb?: string;
  readonly children: ReactNode;
}

/** `bg-white border rounded-xl p-3.5 flex flex-col justify-between shadow-xs` */
export function ModuleColumn({ icon, tone = 'teal', title, blurb, children }: ModuleColumnProps) {
  return (
    <div className={styles.moduleColumn}>
      <div className={styles.moduleColumnBody}>
        <div className={styles.moduleColumnHead}>
          <span
            className={[styles.moduleColumnIcon, TONE_CLASS[tone]].join(' ')}
            aria-hidden="true"
          >
            <Icon name={icon} size={16} />
          </span>
          <h3 className={styles.moduleColumnTitle}>{title}</h3>
        </div>
        {blurb !== undefined && <p className={styles.moduleColumnBlurb}>{blurb}</p>}
        <ul className={styles.moduleLinks}>{children}</ul>
      </div>
    </div>
  );
}

export interface ModuleLinkProps {
  readonly icon: IconName;
  readonly tone?: AccentTone;
  readonly label: string;
  readonly onClick: () => void;
}

/**
 * `a.flex items-center justify-between p-1.5 rounded` with a chevron.
 *
 * A BUTTON AND NOT AN ANCHOR, because the destination is reached through the
 * router rather than a document load — and an `href="#"` that navigates by
 * script is a link that lies to a middle-click.
 */
export function ModuleLink({ icon, tone = 'info', label, onClick }: ModuleLinkProps) {
  return (
    <li>
      <button type="button" className={styles.moduleLink} onClick={onClick}>
        <span className={styles.moduleLinkBody}>
          <span className={[styles.moduleLinkIcon, TONE_CLASS[tone]].join(' ')} aria-hidden="true">
            <Icon name={icon} size={14} />
          </span>
          <span className={styles.moduleLinkLabel}>{label}</span>
        </span>
        <span className={styles.moduleLinkChevron} aria-hidden="true">
          <Icon name="caret-right" size={12} />
        </span>
      </button>
    </li>
  );
}

/* =========================================================================
   THE RECENT RECORDS ROW — two record sets, side by side
   ========================================================================= */

/** `section.grid grid-cols-1 lg:grid-cols-2 gap-4` */
export function RecordRow({ children }: { readonly children: ReactNode }) {
  return <section className={styles.recordRow}>{children}</section>;
}

export interface RecordCardProps {
  readonly icon: IconName;
  readonly tone?: AccentTone;
  readonly title: string;
  /** The reference's "View all →". Absent where the caller may not follow it. */
  readonly actionLabel?: string;
  readonly onAction?: () => void;
  readonly children: ReactNode;
}

/** `rounded-xl overflow-hidden` with a `px-4 py-3 border-b` header bar. */
export function RecordCard({
  icon,
  tone = 'success',
  title,
  actionLabel,
  onAction,
  children,
}: RecordCardProps) {
  return (
    <div className={styles.recordCard}>
      <div className={styles.recordHead}>
        <span className={styles.recordTitleGroup}>
          <span className={[styles.recordIcon, TONE_CLASS[tone]].join(' ')} aria-hidden="true">
            <Icon name={icon} size={16} />
          </span>
          <h3 className={styles.recordTitle}>{title}</h3>
        </span>
        {actionLabel !== undefined && onAction !== undefined && (
          <button type="button" className={styles.recordAction} onClick={onAction}>
            <span>{actionLabel}</span>
            <Icon name="caret-right" size={14} />
          </button>
        )}
      </div>
      <div className={styles.recordBody}>{children}</div>
    </div>
  );
}
